import { useEffect, useRef, useState } from "react";
import { useClerk, useOrganization, useSession, useUser } from "@clerk/clerk-react";

type Failure = "load" | "activation";
type MembershipListState = {
  isLoading: boolean;
  count?: number;
  defaultOrganizationId?: string;
  error: unknown;
  revalidate?: () => Promise<unknown>;
};

export function useOrganizationInitialization({
  isLoading, count, defaultOrganizationId, error, revalidate,
}: MembershipListState) {
  const clerk = useClerk();
  const { user } = useUser();
  const { session } = useSession();
  const { organization } = useOrganization();
  const userId = user?.id;
  const sessionId = session?.id;
  const sessionKey = `${userId}:${sessionId}`;
  const [failure, setFailure] = useState<{ sessionKey: string; error: Failure }>();
  // Capture revalidate only when Retry is clicked, rather than rerun for each new function reference.
  const [retryRequest, setRetryRequest] = useState<{ sessionKey: string; revalidate: MembershipListState["revalidate"] }>();
  const retry = retryRequest?.sessionKey === sessionKey ? retryRequest : undefined;
  const work = useRef<{
    sessionKey: string;
    retry?: typeof retry;
    reload?: Promise<boolean>;
    activation?: Promise<boolean>;
  }>();
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!clerk.loaded || !userId || !sessionId) return;
    let cancelled = false;
    const sameSession = () => mounted.current && clerk.loaded && clerk.user?.id === userId &&
      clerk.session?.id === sessionId && clerk.session.user.id === userId;
    const isCurrent = () => !cancelled && sameSession();
    const isMember = (id: string) => !!clerk.user?.organizationMemberships.some(m => m.organization.id === id);
    const cacheBroken = () => {
      const id = clerk.organization?.id ?? clerk.session?.lastActiveOrganizationId;
      return id ? !isMember(id) : (count ?? 0) > 0 && !clerk.user?.organizationMemberships.length;
    };
    const report = (error?: Failure) => {
      if (!isCurrent()) return;
      setFailure(previous => error
        ? previous?.sessionKey === sessionKey && previous.error === error ? previous : { sessionKey, error }
        : undefined);
    };

    const initialize = async () => {
      const previous = work.current;
      // Keep pending work across renders, including SDK updates published before a request settles.
      await Promise.all([previous?.reload, previous?.activation]);
      if (!isCurrent()) return;
      if (previous?.sessionKey !== sessionKey || previous.retry !== retry) work.current = { sessionKey, retry };
      const current = work.current!;
      let refreshed = true;
      if (cacheBroken() || retry) {
        current.reload ??= (async () => {
          try {
            await clerk.user!.reload();
            if (sameSession() && retry) await retry.revalidate?.();
            return true;
          } catch { return false; }
        })();
        refreshed = await current.reload;
        if (!isCurrent()) return;
      }

      // Use live SDK data after the request; its return value may be an older resource.
      if (clerk.organization?.id) {
        if (!isMember(clerk.organization.id)) { report("load"); return; }
        report();
        work.current = { sessionKey };
        setRetryRequest(undefined);
        return;
      }
      if (!refreshed || error) { report("load"); return; }
      if (isLoading) return;
      if (!count) { report(); return; }
      const savedId = clerk.session!.lastActiveOrganizationId;
      const target = savedId && isMember(savedId) ? savedId : defaultOrganizationId;
      if (!target || !isMember(target)) { report("load"); return; }
      current.activation ??= clerk.setActive({ organization: target }).then(() => true, () => false);
      const activated = await current.activation;
      if (isCurrent()) report(activated || clerk.organization?.id ? undefined : "activation");
    };

    void initialize();
    return () => { cancelled = true; };
  }, [clerk, clerk.loaded, user, userId, session, sessionId, sessionKey, organization?.id,
    isLoading, count, defaultOrganizationId, error, retry]);

  return {
    error: failure?.sessionKey === sessionKey ? failure.error : undefined,
    retry: () => { setFailure(undefined); setRetryRequest({ sessionKey, revalidate }); },
  };
}
